import { useCallback, useMemo, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import { X } from 'lucide-react';
import type { ImageCropSpec } from '../../utils/media/imageCrop';

interface ImageCropModalProps {
  open: boolean;
  imageSrc: string;
  spec: ImageCropSpec;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (croppedAreaPixels: Area) => void;
}

export function ImageCropModal({
  open,
  imageSrc,
  spec,
  loading = false,
  onCancel,
  onConfirm,
}: ImageCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  if (!open || !spec) return null;

  const recommendedText = useMemo(
    () => `Anbefalt ${spec.recommendedWidth}x${spec.recommendedHeight}.`,
    [spec.recommendedWidth, spec.recommendedHeight]
  );

  const handleCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirmClick = useCallback(() => {
    if (!croppedAreaPixels) return;
    onConfirm(croppedAreaPixels);
  }, [croppedAreaPixels, onConfirm]);

  return (
    <div className="fixed inset-0 talentfunn-modal-overlay bg-black/60 backdrop-blur-sm z-[300] flex items-center justify-center p-4 pt-page-safe pb-safe">
      <div className="w-full max-w-2xl rounded-2xl bg-white overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-gray-900 font-semibold">{spec.title}</h3>
            <p className="text-xs text-gray-500">
              {spec.helpText || recommendedText}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
            aria-label="Lukk beskjæring"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="relative w-full h-[52vh] bg-gray-900">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            cropShape={spec.cropShape || 'rect'}
            aspect={spec.aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
            showGrid
          />
        </div>

        <div className="px-4 py-4 border-t border-gray-200 space-y-4">
          <div>
            <label className="block text-sm text-gray-700 mb-2">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              disabled={loading}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={handleConfirmClick}
              disabled={loading || !croppedAreaPixels}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-md disabled:opacity-50"
            >
              {loading ? 'Behandler...' : 'Bruk utsnitt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
